'use client';

import {
  type ComponentType,
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import useDeferUntilIdle from '@/hooks/use-defer-until-idle';
import { Filter } from '@/types/product-filter';

type ProductFilterProps = { filter: Filter };

type ProductFilterLazyContextValue = {
  FilterComponent: ComponentType<ProductFilterProps> | null;
  isDeferReady: boolean;
};

const ProductFilterLazyContext = createContext<ProductFilterLazyContextValue>({
  FilterComponent: null,
  isDeferReady: false,
});

let cachedFilterComponent: ComponentType<ProductFilterProps> | null = null;

export const useProductFilterLazy = () => useContext(ProductFilterLazyContext);

export const ProductFilterLazyProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isDeferReady = useDeferUntilIdle({ timeout: 2500 });
  const [FilterComponent, setFilterComponent] = useState<ComponentType<ProductFilterProps> | null>(
    () => cachedFilterComponent,
  );

  useEffect(() => {
    if (!isDeferReady || FilterComponent) {
      return;
    }

    void import('./product-filter').then(module => {
      cachedFilterComponent = module.default;
      setFilterComponent(() => module.default);
    });
  }, [isDeferReady, FilterComponent]);

  return (
    <ProductFilterLazyContext.Provider value={{ FilterComponent, isDeferReady }}>
      {children}
    </ProductFilterLazyContext.Provider>
  );
};
